import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { BrandCheckPageHeader } from '@/components/brandcheck/BrandCheckPageHeader';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { RetryImage } from '@/components/ui/RetryImage';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { Select } from '@/components/ui/Select';
import { CompactSortMenu, type CompactSortOption } from '@/components/ui/CompactSortMenu';
import { filterTriggerHover } from '@/components/ui/filterTriggerStyles';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { ChannelMultiSelect } from '@/components/ui/ChannelMultiSelect';
import { HoverCard } from '@/components/ui/HoverCard';
import { Tooltip } from '@/components/ui/Tooltip';
import { CarouselThumb } from '@/components/calendar/PostPopover/CarouselThumb';
import { CaptionWithReadMore } from '@/components/calendar/PostPopover/CaptionWithReadMore';
import { ICONS } from '@/constants/icons';
import { isSocialPublishTarget } from '@/constants/platforms';
import { cn } from '@/utils/cn';
import { getChannelAvatar } from '@/utils/channel-avatar';
import { useCalendars } from '@/hooks/useCalendar';
import { useChannels } from '@/hooks/useChannels';
import { useBrandCheckCalendar, useBrandCheckChannel } from '@/hooks/useBrandCheck';
import { getStatusStyle, getStatusBadge } from '@/utils/calendarStatus';
import { useTimezone } from '@/contexts/TimezoneContext';
import { BrandCheckItem, BrandCheckStatus } from '@/types/brand-check';
import { formatApiError } from '@/services/api';
import { PostCreator } from '@/components/PostCreator';
import { VariationCreator } from '@/components/VariationCreator';
import { variationsService } from '@/services/variations.service';
import { PostVariation } from '@/types/variations';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useEscapeLayer } from '@/hooks/useEscapeLayer';
import { useDrawerFocus } from '@/hooks/useDrawerFocus';

// Platform info for channel icons
interface PlatformInfo {
  color: string;
  icon: IconDefinition;
  name: string;
}

const PLATFORM_INFO: Record<string, PlatformInfo> = {
  LINKEDIN: { color: '#0A66C2', icon: faLinkedin, name: 'LinkedIn' },
  FACEBOOK: { color: '#1877F2', icon: faFacebook, name: 'Facebook' },
  INSTAGRAM: { color: '#E4405F', icon: faInstagram, name: 'Instagram' },
  TWITTER: { color: '#000000', icon: faXTwitter, name: 'X' },
  YOUTUBE: { color: '#FF0000', icon: faYoutube, name: 'YouTube' },
  TIKTOK: { color: '#000000', icon: faTiktok, name: 'TikTok' },
  PINTEREST: { color: '#E60023', icon: faPinterest, name: 'Pinterest' },
  THREADS: { color: '#000000', icon: faThreads, name: 'Threads' },
  GOOGLE_MY_BUSINESS: { color: '#4285F4', icon: faGoogle, name: 'Google' },
  BLUESKY: { color: '#0085FF', icon: faBluesky, name: 'Bluesky' },
};

const getPlatformInfo = (platform: string): PlatformInfo => {
  return PLATFORM_INFO[platform] || { color: '#6B7280', icon: faGlobe, name: platform };
};

const DEFAULT_ZOOM_MOBILE = 80;
const DEFAULT_ZOOM_DESKTOP = 140;
const DEFAULT_FEED_ZOOM_DESKTOP = 360;

/**
 * Get opacity class based on status
 * MAPPED = 60% opacity, no border (planning stage, ghosted look)
 * SCHEDULED = 100% opacity + dotted border (committed but not yet published)
 * PUBLISHED = 100% + solid green border (live)
 * PARTIAL = 100% + solid amber border (partially published)
 * FAILED = 100% + solid red border (error)
 * CANCELLED = 60% opacity, ghosted like MAPPED (occurrence never actually
 * happened, matches STATUS_STYLES.CANCELLED's muted-gray convention in
 * utils/calendarStatus.ts)
 */
const getStatusOpacity = (status: BrandCheckStatus): string => {
  switch (status) {
    case 'MAPPED':
    case 'CANCELLED':
      return 'opacity-60';
    case 'SCHEDULED':
    case 'PUBLISHED':
    case 'PARTIAL':
    case 'FAILED':
    default:
      return 'opacity-100';
  }
};

/**
 * Get border style for status
 * Each status has a distinct border to visually differentiate at a glance
 */
const getStatusBorder = (status: BrandCheckStatus): string => {
  switch (status) {
    case 'MAPPED':
      return '';
    case 'SCHEDULED':
      return 'border-2 border-dashed border-gray-300';
    case 'PUBLISHED':
      return 'border-2 border-emerald-500';
    case 'PARTIAL':
      return 'border-2 border-amber-500';
    case 'FAILED':
      return 'border-2 border-red-500';
    case 'CANCELLED':
      return 'border-2 border-dashed border-gray-300';
    default:
      return '';
  }
};

/**
 * Get status indicator for the card corner
 */
const getStatusIndicator = (status: BrandCheckStatus) => {
  switch (status) {
    case 'MAPPED':
      return { icon: ICONS.CALENDAR, color: 'text-gray-600', bg: 'bg-gray-100' };
    case 'SCHEDULED':
      return { icon: ICONS.CLOCK, color: 'text-teal-600', bg: 'bg-teal-50' };
    case 'PUBLISHED':
      return { icon: ICONS.CHECK, color: 'text-green-600', bg: 'bg-green-50' };
    case 'PARTIAL':
      return { icon: ICONS.PARTIAL, color: 'text-amber-600', bg: 'bg-amber-50' };
    case 'FAILED':
      return { icon: ICONS.CROSS, color: 'text-red-600', bg: 'bg-red-50' };
    case 'CANCELLED':
      return { icon: ICONS.CROSS, color: 'text-gray-500', bg: 'bg-gray-100' };
    default:
      return { icon: ICONS.FILE, color: 'text-gray-400', bg: 'bg-gray-50' };
  }
};

/**
 * Per-channel status label key (Task #182 R2-6 #2).
 *
 * `ch.status` (channel-avatar tooltip in the Channels section) is NOT a
 * `BrandCheckStatus` — it's the raw `ChannelInstanceStatus` the backend
 * copies straight from `ScheduledChannelInstance.status` (PENDING/
 * PUBLISHING/PUBLISHED/FAILED/RETRYING — see `brand-check.service.ts` Path B)
 * whenever it has real per-channel publish data. Map every value to its
 * translated `common:` label so the tooltip never renders a raw enum;
 * `status_unknown` is the defensive fallback for anything outside this set.
 */
const CHANNEL_STATUS_LABEL_KEYS: Record<string, string> = {
  PENDING: 'common:status_pending',
  PUBLISHING: 'common:status_publishing',
  PUBLISHED: 'common:status_published',
  FAILED: 'common:status_failed',
  RETRYING: 'common:retrying',
  SCHEDULED: 'common:status_scheduled',
};

const getChannelStatusLabel = (status: string, t: (key: string) => string): string =>
  t(CHANNEL_STATUS_LABEL_KEYS[status] ?? 'common:status_unknown');

/**
 * Brand Check Page
 *
 * Visual consistency checker - see how posts look together.
 *
 * Features:
 * - Two view modes: Calendar View (grid) and Channel View (feed for single, grid for multiple)
 * - Status differentiation: MAPPED=60% opacity, SCHEDULED=dotted border, PUBLISHED=green border, FAILED=red border
 * - Hover popover with post details (grid), tooltip on status badge for failures
 * - Infinite scroll pagination
 */
export const BrandCheck = () => {
  const { t } = useTranslation(['calendar', 'common']);
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();

  // View mode
  const [viewMode, setViewMode] = useState<'calendar' | 'channel'>('calendar');
  const explicitViewChoice = useRef(false);

  // Filters
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<number[]>([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<BrandCheckStatus | undefined>();
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('oldest');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const sortOptions: CompactSortOption<'newest' | 'oldest'>[] = [
    { value: 'newest', label: t('newest_first', { defaultValue: 'Newest' }), icon: ICONS.CALENDAR, direction: 'down' },
    { value: 'oldest', label: t('oldest_first', { defaultValue: 'Oldest' }), icon: ICONS.CALENDAR, direction: 'up' },
  ];

  // Post/Variation editor modals
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  const [showPostCreator, setShowPostCreator] = useState(false);
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [showVariationCreator, setShowVariationCreator] = useState(false);
  const [editingVariation, setEditingVariation] = useState<PostVariation | null>(null);
  const [isLoadingVariation, setIsLoadingVariation] = useState(false);

  // Update sort order default when view mode changes
  useEffect(() => {
    setSortOrder(viewMode === 'calendar' ? 'oldest' : 'newest');
  }, [viewMode]);

  // Follow the available source when filters change, but preserve an explicit
  // view choice so an unavailable view can guide the user to its source filter.
  useEffect(() => {
    if (explicitViewChoice.current) return;
    if (selectedChannelIds.length > 0 && selectedCalendarIds.length === 0) {
      setViewMode('channel');
    } else if (selectedCalendarIds.length > 0 && selectedChannelIds.length === 0) {
      setViewMode('calendar');
    }
  }, [selectedCalendarIds.length, selectedChannelIds.length]);

  // Close filter drawer on ESC (topmost layer only; inner popovers consume the key first)
  const closeFilterDrawer = useCallback(() => setIsFilterDrawerOpen(false), []);
  useEscapeLayer(isFilterDrawerOpen, closeFilterDrawer);
  const filterDrawerRef = useRef<HTMLDivElement>(null);
  useDrawerFocus(isFilterDrawerOpen, filterDrawerRef);

  // Prevent body scroll when drawer is open
  useBodyScrollLock(isFilterDrawerOpen);

  // Remember thumbnail-grid and single-channel feed sizes independently.
  const [gridZoomLevel, setGridZoomLevel] = useState<number>(
    () => window.innerWidth < 768 ? DEFAULT_ZOOM_MOBILE : DEFAULT_ZOOM_DESKTOP
  );
  const [feedZoomLevel, setFeedZoomLevel] = useState(DEFAULT_FEED_ZOOM_DESKTOP);

  // Fetch calendars and channels for filters
  const { data: calendars } = useCalendars({ isActive: true });
  const { data: channels } = useChannels();

  // Auto-select first calendar on initial load so the page isn't empty
  useEffect(() => {
    if (calendars && calendars.length > 0 && !explicitViewChoice.current && selectedCalendarIds.length === 0 && selectedChannelIds.length === 0) {
      setSelectedCalendarIds([calendars[0].id]);
    }
  }, [calendars, selectedCalendarIds.length, selectedChannelIds.length]);

  // Data hooks - pass both filters for cross-filtering
  const calendarQuery = useBrandCheckCalendar(selectedCalendarIds, {
    channelIds: selectedChannelIds.length > 0 ? selectedChannelIds : undefined,
    status: statusFilter,
    sort: sortOrder,
    enabled: viewMode === 'calendar' && selectedCalendarIds.length > 0,
  });

  const channelQuery = useBrandCheckChannel(selectedChannelIds, {
    calendarIds: selectedCalendarIds.length > 0 ? selectedCalendarIds : undefined,
    status: statusFilter,
    sort: sortOrder,
    enabled: viewMode === 'channel' && selectedChannelIds.length > 0,
  });

  // Get items from active query
  const items =
    viewMode === 'calendar'
      ? calendarQuery.data?.pages.flatMap((p) => p.items) ?? []
      : channelQuery.data?.pages.flatMap((p) => p.items) ?? [];

  const isLoading =
    viewMode === 'calendar' ? calendarQuery.isLoading : channelQuery.isLoading;

  const isError =
    viewMode === 'calendar' ? calendarQuery.isError : channelQuery.isError;
  const queryError =
    viewMode === 'calendar' ? calendarQuery.error : channelQuery.error;
  const hasMore =
    viewMode === 'calendar' ? calendarQuery.hasNextPage : channelQuery.hasNextPage;

  // Infinite scroll
  const { ref: loadMoreRef } = useInView({
    onChange: (inView) => {
      if (inView && hasMore) {
        if (viewMode === 'calendar') calendarQuery.fetchNextPage();
        else channelQuery.fetchNextPage();
      }
    },
  });

  // Determine if channel view should show feed layout (single channel) or grid (multiple)
  const isMobileDevice = typeof window !== 'undefined' && window.innerWidth < 1024;
  const showFeedLayout = viewMode === 'channel' && selectedChannelIds.length === 1 && !isMobileDevice;
  const zoomLevel = showFeedLayout ? feedZoomLevel : gridZoomLevel;
  const setZoomLevel = showFeedLayout ? setFeedZoomLevel : setGridZoomLevel;

  // Handlers
  const handleItemClick = async (item: BrandCheckItem) => {
    if (item.isVariation && item.variationId) {
      // Fetch the full variation object, then open VariationCreator
      setIsLoadingVariation(true);
      try {
        const variation = await variationsService.getVariation(item.postId, item.variationId);
        setEditingVariation(variation);
        setShowVariationCreator(true);
      } catch {
        // Fallback: navigate to creator page
        navigate(`/creator?edit=${item.postId}`);
      } finally {
        setIsLoadingVariation(false);
      }
    } else {
      // Regular post: open PostCreator
      setEditingPostId(item.postId);
      setShowPostCreator(true);
    }
  };

  const clearFilters = () => {
    setSelectedCalendarIds([]);
    setSelectedChannelIds([]);
  };

  const handleClosePostCreator = () => {
    setShowPostCreator(false);
    setEditingPostId(null);
  };

  const handleCloseVariationCreator = () => {
    setShowVariationCreator(false);
    setEditingVariation(null);
  };

  const handleEditorSuccess = () => {
    // Invalidate brand check data to reflect changes
    if (viewMode === 'calendar') {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.brandCheck.calendar(teamId) });
    } else {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.brandCheck.channel(teamId) });
    }
    handleClosePostCreator();
    handleCloseVariationCreator();
  };

  // Build filter options
  const calendarOptions =
    calendars?.map((cal) => ({
      id: cal.id,
      name: cal.name,
    })) || [];

  const channelOptions =
    channels
      // Brand Check reviews SOCIAL posts only — exclude non-social asset/blog
      // channels (Canva, Google Drive, WordPress) from the filter options.
      ?.filter((ch) => isSocialPublishTarget(ch.platform))
      .map((ch) => ({
        id: ch.id,
        name: ch.platformUsername || `Channel ${ch.id}`,
        platform: ch.platform,
        avatarUrl: getChannelAvatar(ch.metadata),
      })) || [];

  // Status options - including PARTIAL for partially failed posts and
  // CANCELLED for occurrences whose every scheduled attempt was cancelled
  // (Task #182: previously mis-surfaced as FAILED)
  const statusOptions = [
    { value: 'MAPPED', label: t('status_mapped', { defaultValue: 'Mapped' }) },
    { value: 'SCHEDULED', label: t('status_scheduled', { defaultValue: 'Scheduled' }) },
    { value: 'PUBLISHED', label: t('status_published', { defaultValue: 'Published' }) },
    { value: 'PARTIAL', label: t('status_partial', { defaultValue: 'Partial success' }) },
    { value: 'FAILED', label: t('status_failed', { defaultValue: 'Failed' }) },
    { value: 'CANCELLED', label: t('status_cancelled', { defaultValue: 'Cancelled' }) },
  ];

  const hasFilterSelection =
    viewMode === 'calendar' ? selectedCalendarIds.length > 0 : selectedChannelIds.length > 0;

  const activeFilterCount = selectedCalendarIds.length + selectedChannelIds.length;

  // Render a single post item with HoverCard popover
  const renderPostItem = (item: BrandCheckItem, isFeedLayout: boolean) => {
    const statusIndicator = getStatusIndicator(item.status);
    const statusStyle = getStatusStyle(item.status);

    const cardContent = (
      <div
        className={cn(
          'relative cursor-pointer overflow-hidden transition-all duration-200 group',
          'hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-bs-secondary focus:ring-offset-2',
          getStatusOpacity(item.status),
          getStatusBorder(item.status),
          isFeedLayout ? 'rounded-xl bg-white' : 'rounded-lg',
        )}
        style={
          isFeedLayout
            ? undefined
            : { aspectRatio: '1/1' }
        }
        onClick={() => handleItemClick(item)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleItemClick(item);
          }
        }}
        aria-label={item.caption?.substring(0, 50) || t('post_thumbnail', { defaultValue: 'Post thumbnail' })}
      >
        {/* Feed layout - vertical card like Instagram/Facebook */}
        {isFeedLayout ? (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 p-3 border-b border-gray-100">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center',
                  statusIndicator.bg
                )}
              >
                <Icon name={statusIndicator.icon} size="sm" className={statusIndicator.color} ariaHidden />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {item.calendarName || 'Post'}
                </div>
                <div className="text-xs text-gray-500">
                  {item.scheduledAt
                    ? formatDateTime(item.scheduledAt)
                    : item.plannedDate
                    ? formatDateTime(item.plannedDate)
                    : t('no_date', { defaultValue: 'No date' })}
                </div>
              </div>
              {item.isVariation && (
                <span className="px-2 py-0.5 rounded bg-soft-coral text-secondary-700 text-xs font-medium">
                  V
                </span>
              )}
            </div>

            {/* Image */}
            <div className="aspect-square bg-gray-100">
              {item.thumbnailUrl ? (
                <RetryImage src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <PlaceholderImage />
              )}
              {/* Video indicator */}
              {item.mediaType === 'VIDEO' && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                    <span className="text-white text-lg ml-0.5">▶</span>
                  </div>
                </div>
              )}
              {/* Carousel indicator */}
              {item.mediaType === 'CAROUSEL' && (
                <div className="absolute top-3 right-3">
                  <Icon name={ICONS.COPY} size="sm" color="text-white" className="drop-shadow-lg" ariaHidden />
                </div>
              )}
            </div>

            {/* Caption */}
            <div className="p-3">
              <p className="text-sm text-gray-700 line-clamp-3">{item.caption || t('no_caption', { defaultValue: 'No caption' })}</p>
            </div>

            {/* Footer with status */}
            <div className="px-3 pb-3 flex items-center justify-between">
              {(item.status === 'FAILED' || item.status === 'PARTIAL') && item.failureReasons && item.failureReasons.length > 0 ? (
                <Tooltip
                  content={
                    <div className="text-xs space-y-1">
                      <p className="font-medium">{getStatusBadge(item.status, t).label}</p>
                      {item.failureReasons.map((reason, i) => (
                        <p key={i} className="text-red-300">{reason}</p>
                      ))}
                    </div>
                  }
                  side="top"
                >
                  <span
                    className={cn(
                      'px-2 py-1 rounded-full text-xs font-medium cursor-help',
                      statusStyle.bgColor,
                      statusStyle.color
                    )}
                  >
                    {getStatusBadge(item.status, t).label}
                  </span>
                </Tooltip>
              ) : (
                <span
                  className={cn(
                    'px-2 py-1 rounded-full text-xs font-medium',
                    statusStyle.bgColor,
                    statusStyle.color
                  )}
                >
                  {getStatusBadge(item.status, t).label}
                </span>
              )}
              {item.channels.length > 0 && (
                <span className="text-xs text-gray-500">
                  {item.channels.length} {item.channels.length === 1 ? 'channel' : 'channels'}
                </span>
              )}
            </div>
          </>
        ) : (
          /* Grid layout - compact square thumbnails */
          <>
            {/* Image or placeholder */}
            {item.thumbnailUrl ? (
              <RetryImage src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <PlaceholderImage />
            )}

            {/* Gradient overlay at bottom */}
            <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/50 to-transparent" />

            {/* Status indicator (top-right) - small icon, with tooltip for failures */}
            {(item.status === 'FAILED' || item.status === 'PARTIAL') && item.failureReasons && item.failureReasons.length > 0 ? (
              <Tooltip
                content={
                  <div className="text-xs space-y-1">
                    <p className="font-medium">{getStatusBadge(item.status, t).label}</p>
                    {item.failureReasons.map((reason, i) => (
                      <p key={i} className="text-red-300">{reason}</p>
                    ))}
                  </div>
                }
                side="left"
              >
                <div
                  className={cn(
                    'absolute top-1 right-1 w-4 h-4 lg:top-1.5 lg:right-1.5 lg:w-6 lg:h-6 rounded-md flex items-center justify-center',
                    'bg-white/90 backdrop-blur-sm shadow-sm cursor-help'
                  )}
                >
                  <Icon name={statusIndicator.icon} size="custom" className={`!text-[8px] lg:!text-[14px] ${statusIndicator.color}`} ariaHidden />
                </div>
              </Tooltip>
            ) : (
              <div
                className={cn(
                  'absolute top-1 right-1 w-4 h-4 lg:top-1.5 lg:right-1.5 lg:w-6 lg:h-6 rounded-md flex items-center justify-center',
                  'bg-white/90 backdrop-blur-sm shadow-sm'
                )}
              >
                <Icon name={statusIndicator.icon} size="custom" className={`!text-[8px] lg:!text-[14px] ${statusIndicator.color}`} ariaHidden />
              </div>
            )}

            {/* Video indicator */}
            {item.mediaType === 'VIDEO' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                  <span className="text-white text-sm ml-0.5">▶</span>
                </div>
              </div>
            )}

            {/* Carousel indicator */}
            {item.mediaType === 'CAROUSEL' && (
              <div className="absolute bottom-1.5 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                {Array.from({ length: Math.min(item.mediaCount, 5) }).map((_, i) => (
                  <div key={i} className="w-1 h-1 rounded-full bg-white/80 shadow-sm" />
                ))}
              </div>
            )}

            {/* Variation badge */}
            {item.isVariation && (
              <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded bg-secondary-500 text-secondary-500-contrast text-[10px] font-bold flex items-center justify-center">
                V
              </div>
            )}
          </>
        )}
      </div>
    );

    // Wrap with HoverCard for popover (only for grid layout, feed layout has enough info)
    if (isFeedLayout) {
      return <div key={item.id}>{cardContent}</div>;
    }

    return (
      <HoverCard
        key={item.id}
        trigger={cardContent}
        side="right"
        align="start"
        sideOffset={8}
      >
        {/* Popover Content */}
        <div className="max-h-full lg:max-h-[70vh] h-full lg:h-auto flex flex-col">
          {/* Header with gradient based on status */}
          <div className={cn('bg-gradient-to-r px-4 pr-14 lg:pr-4 py-3 flex-shrink-0', statusStyle.gradient)}>
            <div className="flex items-center gap-2">
              <Icon
                name={statusIndicator.icon}
                size="sm"
                className={statusStyle.headerTextColor}
                ariaHidden
              />
              <span className={cn('font-medium text-sm', statusStyle.headerTextColor)}>
                {getStatusBadge(item.status, t).label}
              </span>
              {/* eslint-disable i18next/no-literal-string */}
              {item.isVariation && (
                <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-white/20 text-white">
                  Variation
                </span>
              )}
              {/* eslint-enable i18next/no-literal-string */}
            </div>
            {/* Timing info */}
            <div className={cn('text-xs mt-1', statusStyle.headerTextColor, 'opacity-80')}>
              {item.publishedAt
                ? `Published ${formatDateTime(item.publishedAt)}`
                : item.scheduledAt
                ? `Scheduled for ${formatDateTime(item.scheduledAt)}`
                : item.plannedDate
                ? `Planned for ${formatDateTime(item.plannedDate)}`
                : 'No date set'}
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto min-h-0">
          {/* Carousel Thumbnail */}
          <div className="p-[9px]">
            <CarouselThumb
              media={(item.postMedia || []).map(m => ({
                id: m.id,
                type: m.type as 'IMAGE' | 'VIDEO' | 'PDF',
                thumbnailUrl: m.thumbnailUrl,
                originalUrl: m.originalUrl,
              }))}
              mediaCount={item.mediaCount}
              firstMedia={item.postMedia?.[0] ? {
                id: item.postMedia[0].id,
                type: item.postMedia[0].type as 'IMAGE' | 'VIDEO' | 'PDF',
                thumbnailUrl: item.postMedia[0].thumbnailUrl,
                originalUrl: item.postMedia[0].originalUrl,
              } : undefined}
            />
          </div>

          {/* Caption */}
          <div className="p-4">
            <CaptionWithReadMore caption={item.caption || ''} />

            {/* Scheduled date */}
            {item.scheduledAt && (
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                <Icon name={ICONS.CALENDAR} size="xs" ariaHidden />
                <span>{formatDateTime(item.scheduledAt)}</span>
              </div>
            )}

            {/* Channels - with profile photos, platform badges, and per-channel status tooltips */}
            {item.channels.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  {t('channels', { defaultValue: 'Channels' })} ({item.channels.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {item.channels.slice(0, 6).map((ch) => {
                    const platformInfo = getPlatformInfo(ch.platform);
                    const channelRingColors: Record<string, string> = {
                      PUBLISHED: 'ring-emerald-500',
                      FAILED: 'ring-red-500',
                      PENDING: 'ring-amber-400',
                      SCHEDULED: 'ring-blue-500',
                      PUBLISHING: 'ring-yellow-500',
                    };
                    const ringColor = ch.status
                      ? (channelRingColors[ch.status] ?? 'ring-gray-200')
                      : 'ring-gray-200';
                    const tooltipContent = (
                      <div className="text-xs">
                        <p className="font-medium">{ch.username || ch.platform}</p>
                        <p className="text-gray-400">{platformInfo.name}</p>
                        {ch.status && (
                          <p className={ch.status === 'FAILED' ? 'text-red-400' : 'text-gray-400'}>
                            {getChannelStatusLabel(ch.status, t)}
                          </p>
                        )}
                        {ch.error && (
                          <p className="text-red-300 mt-1 max-w-xs break-words">{ch.error}</p>
                        )}
                      </div>
                    );
                    return (
                      <Tooltip key={ch.channelId} content={tooltipContent} side="top" className="!z-[10001]">
                        <div className="relative group/channel">
                          {/* Avatar with profile photo or platform icon */}
                          <div className={`w-9 h-9 rounded-full overflow-hidden ring-2 ${ringColor}`}>
                            {ch.profilePhotoUrl ? (
                              <img
                                src={ch.profilePhotoUrl}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div
                                className="w-full h-full flex items-center justify-center"
                                style={{ backgroundColor: platformInfo.color }}
                              >
                                <FontAwesomeIcon
                                  icon={platformInfo.icon}
                                  className="text-white text-sm"
                                />
                              </div>
                            )}
                          </div>
                          {/* Platform badge overlay (bottom-right) */}
                          <div
                            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white"
                            style={{ backgroundColor: platformInfo.color }}
                          >
                            <FontAwesomeIcon
                              icon={platformInfo.icon}
                              className="text-white text-[7px]"
                            />
                          </div>
                          {/* Red dot indicator for FAILED channels */}
                          {ch.status === 'FAILED' && (
                            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 border border-white" />
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}
                  {item.channels.length > 6 && (
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500 font-medium">
                      +{item.channels.length - 6}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Calendar info */}
            {item.calendarName && (
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                <Icon name={ICONS.CALENDAR} size="xs" ariaHidden />
                {item.calendarName}
              </div>
            )}

          </div>

          </div>{/* End scrollable content */}

          {/* Footer */}
          <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-t border-gray-100">
            {/* Desktop: edit button */}
            <button
              onClick={() => handleItemClick(item)}
              className="hidden lg:flex w-full items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-primary-600-contrast text-sm font-medium rounded-lg transition-colors"
            >
              <Icon name={ICONS.EDIT} size="sm" className="text-primary-600-contrast" ariaHidden />
              {t('edit_post', { defaultValue: 'Edit Post' })}
            </button>
            {/* Mobile: action button */}
            <button
              data-close-sheet
              onClick={() => handleItemClick(item)}
              className="lg:hidden w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-primary-600-contrast text-sm font-medium rounded-lg transition-colors"
            >
              <Icon name={ICONS.EDIT} size="sm" className="text-primary-600-contrast" ariaHidden />
              {t('edit_post', { defaultValue: 'Edit Post' })}
            </button>
          </div>
        </div>
      </HoverCard>
    );
  };

  return (
    <>
      {/* Page Header */}
      <BrandCheckPageHeader />

      <div className="space-y-4">
        {/* View and content controls */}
        <div className="rounded-xl bg-white p-3 shadow-card sm:p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div
              className="inline-flex w-full rounded-lg bg-gray-100 p-0.5 sm:w-auto"
              role="group"
              aria-label={t('view_mode', { defaultValue: 'View mode' })}
            >
              <Tooltip
                content={selectedCalendarIds.length === 0 && !isFilterDrawerOpen
                  ? t('choose_calendar_for_view', { defaultValue: 'Choose a calendar in Filters to use Calendar view.' })
                  : null}
                side="bottom"
              >
                <button
                  type="button"
                  onClick={() => {
                    explicitViewChoice.current = true;
                    setViewMode('calendar');
                    if (selectedCalendarIds.length === 0) setIsFilterDrawerOpen(true);
                  }}
                  aria-pressed={viewMode === 'calendar'}
                  className={cn(
                    'flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors sm:h-[30px] sm:flex-none sm:px-3',
                    viewMode === 'calendar'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Icon name={ICONS.CALENDAR} size="sm" ariaHidden />
                  {t('calendar_view', { defaultValue: 'Calendar' })}
                </button>
              </Tooltip>
              <Tooltip
                content={selectedChannelIds.length === 0 && !isFilterDrawerOpen
                  ? t('choose_channel_for_view', { defaultValue: 'Choose a channel in Filters to use Channel view.' })
                  : null}
                side="bottom"
              >
                <button
                  type="button"
                  onClick={() => {
                    explicitViewChoice.current = true;
                    setViewMode('channel');
                    if (selectedChannelIds.length === 0) setIsFilterDrawerOpen(true);
                  }}
                  aria-pressed={viewMode === 'channel'}
                  className={cn(
                    'flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors sm:h-[30px] sm:flex-none sm:px-3',
                    viewMode === 'channel'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Icon name={ICONS.SHARE} size="sm" ariaHidden />
                  {t('channel_view', { defaultValue: 'Channel' })}
                </button>
              </Tooltip>
            </div>

            <div className="flex w-full min-w-0 items-center gap-3 lg:w-auto">
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(true)}
                aria-label={activeFilterCount > 0
                  ? t('filters_selected_count', { count: activeFilterCount, defaultValue: 'Filters, {{count}} selected' })
                  : t('filters', { defaultValue: 'Filters' })}
                className={cn(
                  'flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 transition-colors sm:h-[34px] sm:px-3',
                  filterTriggerHover
                )}
              >
                <Icon name={ICONS.FILTER} size="xs" ariaHidden />
                <span className="max-[360px]:hidden">{t('filters', { defaultValue: 'Filters' })}</span>
                {activeFilterCount > 0 && (
                  <span className="font-semibold text-primary-700" aria-hidden="true">
                    · {activeFilterCount}
                  </span>
                )}
              </button>
              <div className="min-w-0 flex-1 sm:w-44 sm:flex-none">
                <Select
                  value={statusFilter || ''}
                  onValueChange={(value) => setStatusFilter(value ? (value as BrandCheckStatus) : undefined)}
                  options={statusOptions}
                  size="sm"
                  clearable
                  clearLabel={t('all_statuses', { defaultValue: 'All Statuses' })}
                  placeholder={t('all_statuses', { defaultValue: 'All Statuses' })}
                  className="!h-11 sm:!h-[34px]"
                />
              </div>
              <div className="h-11 w-[52px] shrink-0 sm:h-[34px] sm:w-10">
                <CompactSortMenu value={sortOrder} options={sortOptions} onValueChange={setSortOrder} label={t('sort_by')} />
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-xl shadow-card overflow-hidden">
          {hasFilterSelection && !isError && (isLoading || items.length > 0) && (
            <div className="flex items-center justify-end gap-2 px-4 pt-3 pb-0">
              <Icon name={ICONS.MINUS} size="xs" color="text-gray-400" ariaHidden />
              <input
                type="range"
                min={showFeedLayout ? 220 : 60}
                max={showFeedLayout ? 500 : 300}
                value={zoomLevel}
                onChange={(e) => setZoomLevel(Number(e.target.value))}
                className="h-2 min-w-0 flex-1 cursor-pointer appearance-none rounded-lg bg-gray-200 accent-bs-secondary sm:w-32 sm:flex-none"
                aria-label={t('zoom_level', { defaultValue: 'Zoom level' })}
              />
              <Icon name={ICONS.PLUS} size="xs" color="text-gray-400" ariaHidden />
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <span className="w-10 text-right text-xs tabular-nums text-gray-500">{zoomLevel}px</span>
            </div>
          )}
          {isError ? (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-gray-500">
              <Icon name={ICONS.LOCK} size="xl" className="mb-4 text-gray-300" ariaHidden />
              <p className="text-lg font-medium text-gray-700">
                {formatApiError(queryError).message}
              </p>
              <Button
                variant="primary"
                size="sm"
                className="mt-4"
                onClick={() => navigate('/settings?tab=billing')}
              >
                {t('upgrade_plan', { defaultValue: 'Upgrade Plan' })}
              </Button>
            </div>
          ) : !hasFilterSelection ? (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-gray-500">
              <Icon name={ICONS.FILTER} size="xl" className="mb-4 text-gray-300" ariaHidden />
              <p className="text-lg font-medium text-gray-700">
                {viewMode === 'calendar'
                  ? t('select_calendar_prompt', { defaultValue: 'Select a calendar to view posts' })
                  : t('select_channel_prompt', { defaultValue: 'Select a channel to view posts' })}
              </p>
            </div>
          ) : isLoading ? (
            <div
              className="grid gap-4 px-4 pb-4 pt-3"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${zoomLevel}px, 1fr))`,
              }}
            >
              {Array.from({ length: showFeedLayout ? 8 : 24 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-gray-200 animate-pulse rounded-xl"
                  style={{ aspectRatio: showFeedLayout ? '4/5' : '1/1' }}
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-gray-500">
              <Icon name={ICONS.IMAGE} size="xl" className="mb-4 text-gray-300" ariaHidden />
              <p className="text-lg font-medium text-gray-700">
                {t('no_posts_found', { defaultValue: 'No posts found matching your filters' })}
              </p>
            </div>
          ) : (
            <>
              {/* Feed layout for single channel - grid of vertical cards */}
              <div
                className="grid gap-3 px-4 pb-4 pt-3"
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${zoomLevel}px, 1fr))`,
                }}
              >
                {items.map((item) => renderPostItem(item, showFeedLayout))}
              </div>

              {/* Infinite scroll trigger */}
              {hasMore && (
                <div ref={loadMoreRef} className="p-4 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Filter Drawer */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ease-in-out ${
          isFilterDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setIsFilterDrawerOpen(false)}
          aria-hidden="true"
        />

        {/* Drawer */}
        <div
          ref={filterDrawerRef}
          className={`fixed right-0 top-navbar-height bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:bottom-0 w-full sm:max-w-md bg-white shadow-2xl z-50 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
            isFilterDrawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          role="dialog"
          aria-modal={isFilterDrawerOpen ? 'true' : undefined}
          aria-labelledby="brandcheck-filter-drawer-title"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name={ICONS.FILTER} size="sm" color="text-gray-600" />
              <h2 id="brandcheck-filter-drawer-title" className="text-lg font-bold text-gray-900">
                {t('filters', { defaultValue: 'Filters' })}
              </h2>
            </div>
            <button
              onClick={() => setIsFilterDrawerOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('close', { defaultValue: 'Close' })}
            >
              <Icon name={ICONS.CROSS} size="sm" color="text-gray-600" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6 space-y-6">
            {/* Calendar multi-select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('calendars', { defaultValue: 'Calendars' })}
              </label>
              <MultiSelectDropdown
                value={selectedCalendarIds}
                onChange={setSelectedCalendarIds}
                options={calendarOptions}
                placeholder={t('select_calendars', { defaultValue: 'Select calendars...' })}
              />
              {viewMode === 'calendar' && selectedCalendarIds.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  {t('calendar_view_needs_calendar', { defaultValue: 'Choose a calendar to show posts in Calendar view.' })}
                </p>
              )}
            </div>

            {/* Channel multi-select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('channels', { defaultValue: 'Channels' })}
              </label>
              <ChannelMultiSelect
                value={selectedChannelIds}
                onChange={setSelectedChannelIds}
                options={channelOptions}
                placeholder={t('select_channels', { defaultValue: 'Select channels...' })}
              />
              {viewMode === 'channel' && selectedChannelIds.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  {t('channel_view_needs_channel', { defaultValue: 'Choose a channel to show posts in Channel view.' })}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-4 pb-6 sm:pb-4 flex gap-1.5 sm:gap-3">
            <Button
              variant="primary"
              onClick={() => setIsFilterDrawerOpen(false)}
              className="flex-1 w-full"
            >
              {t('apply', { defaultValue: 'Apply' })}
            </Button>
            {activeFilterCount > 0 && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <Icon name={ICONS.CROSS} size="sm" />
                {t('clear_filters', { defaultValue: 'Clear' })}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* PostCreator Modal */}
      <PostCreator
        isOpen={showPostCreator}
        onClose={handleClosePostCreator}
        editingPostId={editingPostId || undefined}
        onSaveSuccess={handleEditorSuccess}
      />

      {/* VariationCreator Modal */}
      <VariationCreator
        isOpen={showVariationCreator}
        onClose={handleCloseVariationCreator}
        variation={editingVariation ?? undefined}
        onSuccess={handleEditorSuccess}
      />

      {/* Loading overlay when fetching variation */}
      {isLoadingVariation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-xl p-6 shadow-lg flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-500 border-r-transparent" />
            <span className="text-sm text-gray-600">Loading...</span>
          </div>
        </div>
      )}
    </>
  );
};
